import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getCoverageFieldSet, getCyberCoverages, formatCurrency, US_STATES, type CoverageLimitDetails } from './acordSmartScanShared';
import '../../styles/AcordSmartScanReviewParts.css';

const FONT = "'Poppins', sans-serif";
const TABLE_PAGE_SIZE = 10;

/* Native <option> popups ignore the page theme by default — setting background/color
 * directly on each <option> fixes it in Chromium/Firefox, but the color pair must
 * itself track the active theme or it looks wrong in the other mode. */
export const getOptionStyle = (theme: 'light' | 'dark'): React.CSSProperties =>
  theme === 'dark' ? { background: '#1a1829', color: '#e2e2f0' } : { background: '#ffffff', color: '#111827' };

type TableSort = { key: string; dir: 'asc' | 'desc' } | null;

function sortRows<T extends Record<string, any>>(rows: T[], sort: TableSort): T[] {
  if (!sort) return rows;
  return [...rows].sort((a, b) => {
    const av = String(a[sort.key] ?? '').toLowerCase();
    const bv = String(b[sort.key] ?? '').toLowerCase();
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });
}

/* ── Titled, sortable, paginated table — shared by the live-scan review and the
 * History preview so both surfaces render Property/Vehicle/Driver identically. ── */
export const SortablePaginatedTable: React.FC<{
  title: string;
  rows: Record<string, any>[];
  columns: Array<{ key: string; label: string }>;
  emptyLabel: string;
}> = ({ title, rows, columns, emptyLabel }) => {
  const [sort, setSort] = useState<TableSort>(null);
  const [page, setPage] = useState(1);

  const sorted = sortRows(rows, sort);
  const totalPages = Math.max(1, Math.ceil(sorted.length / TABLE_PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * TABLE_PAGE_SIZE, page * TABLE_PAGE_SIZE);
  const toggleSort = (key: string) =>
    setSort(sort?.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  return (
    <div className="acord-smartscan-review__panel">
      <h4 className="acord-smartscan-review__title">{title}</h4>
      <div className="acord-smartscan-review__table-card">
        {rows.length === 0 ? (
          <p className="acord-smartscan-review__table-empty">{emptyLabel}</p>
        ) : (
          <>
            <table className="acord-smartscan-review__table">
              <thead>
                <tr className="acord-smartscan-review__head-row">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className="acord-smartscan-review__th"
                    >
                      {col.label} <span className="acord-smartscan-review__sort-icon" style={{ opacity: sort?.key === col.key ? 1 : 0.4 }}>{sort?.key === col.key ? (sort.dir === 'asc' ? '↑' : '↓') : '↑↓'}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => (
                  <tr key={i} className="acord-smartscan-review__row">
                    {columns.map((col) => (
                      <td key={col.key} className="acord-smartscan-review__td">{row[col.key] || '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="acord-smartscan-review__pagination">
                <button className="qp-pagination__btn" disabled={page === 1} onClick={() => setPage(page - 1)}>‹</button>
                <button className="qp-pagination__btn qp-pagination__btn--active">{page}</button>
                <button className="qp-pagination__btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>›</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ── Coverage & Limits card — title (line of business) + a divider-row, 2-column
 * label/value grid, or a Cyber table. Shared by the live-scan review and History preview. ── */
export const CoverageLimitCard: React.FC<{
  lineOfBusiness: string;
  coverageLimitDetails: CoverageLimitDetails | null;
}> = ({ lineOfBusiness, coverageLimitDetails }) => {
  const isCyber = (lineOfBusiness || '').toLowerCase().includes('cyber');
  const cyberCoverages = getCyberCoverages(coverageLimitDetails);
  const coverageFieldSet = coverageLimitDetails ? getCoverageFieldSet(lineOfBusiness) : null;

  return (
    <div className="acord-smartscan-review__panel">
      <h4 className="acord-smartscan-review__title">
        {isCyber ? 'Cyber' : coverageFieldSet?.label || 'Coverage'}
      </h4>
      <div className="acord-smartscan-review__coverage-card">
        {isCyber ? (
          cyberCoverages.length === 0 ? (
            <p className="acord-smartscan-review__coverage-empty">No Coverage Found</p>
          ) : (
            <table className="acord-smartscan-review__cyber-table">
              <thead>
                <tr className="acord-smartscan-review__row">
                  <th className="acord-smartscan-review__cyber-th">Description</th>
                  <th className="acord-smartscan-review__cyber-th">Benefit</th>
                  <th className="acord-smartscan-review__cyber-th">Deductible</th>
                </tr>
              </thead>
              <tbody>
                {cyberCoverages.map((c, i) => (
                  <tr key={i} className="acord-smartscan-review__row">
                    <td className="acord-smartscan-review__td">{c.description || '—'}</td>
                    <td className="acord-smartscan-review__td">{c.benefit || '—'}</td>
                    <td className="acord-smartscan-review__td">{c.deductible || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : coverageFieldSet ? (
          Array.from({ length: Math.ceil(coverageFieldSet.fields.length / 2) }).map((_, rowIdx) => {
            const pair = coverageFieldSet.fields.slice(rowIdx * 2, rowIdx * 2 + 2);
            const isLastRow = rowIdx === Math.ceil(coverageFieldSet.fields.length / 2) - 1;
            return (
              <div
                key={rowIdx}
                className="acord-smartscan-review__coverage-row"
                style={{ borderBottom: isLastRow ? 'none' : '1px solid var(--border-glass)' }}
              >
                {pair.map((f) => (
                  <div key={f.key} className="acord-smartscan-review__coverage-field">
                    <span className="acord-smartscan-review__coverage-label">{f.label}</span>
                    <span className="acord-smartscan-review__coverage-value">{formatCurrency((coverageLimitDetails as any)?.[f.key]) || '—'}</span>
                  </div>
                ))}
              </div>
            );
          })
        ) : (
          <p className="acord-smartscan-review__coverage-empty">No Coverage Found</p>
        )}
      </div>
    </div>
  );
};

/* ── Insured Details field config — shared order/labels between the editable
 * live-scan review grid and the read-only History preview grid. ── */
export const INSURED_FIELD_ORDER: Array<{ key: string; label: string }> = [
  { key: 'email', label: 'Email' },
  { key: 'phoneNumber', label: 'Phone Number' },
  { key: 'policyNumber', label: 'Policy Number' },
  { key: 'policyPremium', label: 'Policy Premium ($)' },
  { key: 'carrierName', label: 'Carrier Name' },
  { key: 'insuredType', label: 'Insured Type' },
  { key: 'lineOfBusiness', label: 'Line of Business' },
  { key: 'quoteNumber', label: 'Quote Number' },
  { key: 'effectiveDate', label: 'Effective Date' },
  { key: 'expirationDate', label: 'Expiration Date' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zipCode', label: 'Zip Code' },
];

const ctrlStyle = (editable: boolean): React.CSSProperties => ({
  width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 12,
  background: editable ? 'var(--surface-glass)' : 'rgba(255,255,255,0.03)',
  border: `1px solid ${editable ? 'var(--border-glass)' : 'rgba(255,255,255,0.06)'}`,
  color: editable ? 'var(--text-primary)' : 'var(--text-secondary)',
  outline: 'none', boxSizing: 'border-box' as const, cursor: editable ? 'text' : 'default',
});

/* ── Insured Details grid — read-only by default (History preview of a synced
 * record); pass isReadOnly={false} + onChange to make it editable (History
 * preview of a not-yet-synced record, submitting the same way the live-scan
 * review does). Mirrors the live-scan review's 3-column field layout. ── */
export const InsuredDetailsReadOnlyGrid: React.FC<{
  insured: Record<string, any>;
  isReadOnly?: boolean;
  onChange?: (patch: Record<string, any>) => void;
  fieldErrors?: Record<string, string>;
}> = ({ insured, isReadOnly = true, onChange, fieldErrors = {} }) => {
  const { theme } = useTheme();
  const optionStyle = getOptionStyle(theme);
  const nameFields = insured.insuredType === 'Personal'
    ? [{ key: 'firstName', label: 'First Name' }, { key: 'lastName', label: 'Last Name' }]
    : [{ key: 'businessName', label: 'Business Name' }];

  const fields = [...nameFields, ...INSURED_FIELD_ORDER];

  if (isReadOnly) {
    return (
      <div className="acord-smartscan-review__insured-grid">
        {fields.map((f) => (
          <div key={f.key}>
            <div className="acord-smartscan-review__insured-field-label">{f.label}</div>
            <div style={ctrlStyle(false)}>{insured[f.key] || '—'}</div>
          </div>
        ))}
      </div>
    );
  }

  const handle = (key: string, value: string) => onChange?.({ [key]: value });

  return (
    <div className="acord-smartscan-review__insured-grid">
      {fields.map((f) => (
        <div key={f.key}>
          <div className="acord-smartscan-review__insured-field-label">{f.label}</div>
          {f.key === 'insuredType' ? (
            <select value={insured.insuredType || 'Commercial'} onChange={(e) => handle('insuredType', e.target.value)} style={ctrlStyle(true)}>
              <option value="Commercial" style={optionStyle}>Commercial</option>
              <option value="Personal" style={optionStyle}>Personal</option>
            </select>
          ) : f.key === 'state' ? (
            <select value={insured.state || ''} onChange={(e) => handle('state', e.target.value)} style={ctrlStyle(true)}>
              <option value="" style={optionStyle}>Select State</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code} style={optionStyle}>{s.name}</option>
              ))}
            </select>
          ) : f.key === 'effectiveDate' || f.key === 'expirationDate' ? (
            <input type="date" value={insured[f.key] || ''} onChange={(e) => handle(f.key, e.target.value)} style={ctrlStyle(true)} />
          ) : (
            <input value={insured[f.key] || ''} onChange={(e) => handle(f.key, e.target.value)} style={ctrlStyle(true)} />
          )}
          {fieldErrors[f.key] && <span className="acord-smartscan-review__field-error">{fieldErrors[f.key]}</span>}
        </div>
      ))}
    </div>
  );
};
